import moment from "moment";
import { PAYROLL_PAYSLIP_PRINT, apiUrlFor } from "../../constants/api";
import ToastMsg from "./ToastMsg";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { normalizePermKey, passesRouteRoleGate } from "../../config/navPermissions";

export const transformUserPermissionArr = (listData) => {
    if (listData?.length > 0) {
        let transformedArray = listData.map(module => {
            let subMenu = [];
            let modulePermission = false;

            // Modules that are genuinely ONE screen, so their permission node is
            // flat (add/edit/permission directly on the module) rather than a
            // module with a subMenu.
            //
            // 'roster' used to be in this list. It is a three-screen module now
            // (Roster, Duty Roster, Shift Change Request -- see
            // administrator.RosterModule), so it goes through the normal branch
            // below and gets a subMenu. Its module-level `permission` becomes the
            // OR of its children, which is what buildDynamicNav gates the sidebar
            // item on, so the menu still appears for exactly the same users.
            if (module.module_name === 'home' ||
                module.module_name === 'dashboard'
            ) {
                let moduleList = module.module_list[module.module_name];
                return {
                    label: module.module_name,
                    permission: moduleList.permission,
                    active: moduleList.active,
                    edit: moduleList.edit,
                    add: moduleList.add,
                    draft: moduleList.draft
                };
            } else {
                for (const [key, value] of Object.entries(module.module_list)) {
                    subMenu.push({
                        label: key,
                        permission: value.permission,
                        add: value.add,
                        edit: value.edit,
                        draft: value.draft,
                        active: value.active
                    });
                    if (value.permission) {
                        modulePermission = true;
                    }
                }
            }
            return {
                label: module.module_name,
                permission: modulePermission,
                subMenu: subMenu
            };
        });
        return transformedArray;
    }
}

const normalize = (str) => str?.toLowerCase().replace(/_/g, " ").trim();

/** Block/Floor/Room/City master screens inherit Designation access for users whose
 *  permission payload predates those module keys (stale session). */
export const patchMasterBlockFloorPermissions = (permissions) => {
    if (!Array.isArray(permissions) || permissions.length === 0) return permissions;

    return permissions.map((menu) => {
        if (normalize(menu.label) !== "master" || !Array.isArray(menu.subMenu)) return menu;

        const designation = menu.subMenu.find((s) => normalize(s.label) === "designation");
        if (!designation?.permission) return menu;

        const subMenu = [...menu.subMenu];
        const desIdx = subMenu.findIndex((s) => normalize(s.label) === "designation");
        let insertAt = desIdx + 1;

        for (const key of ["city", "block", "floor", "room"]) {
            const existingIdx = subMenu.findIndex((s) => normalize(s.label) === key);
            if (existingIdx >= 0) {
                // Keep explicit flags. Copying designation onto a denied row
                // reticked City/Block/Floor/Room after every save.
                continue;
            }
            subMenu.splice(insertAt, 0, { ...designation, label: key });
            insertAt += 1;
        }

        return { ...menu, subMenu };
    });
};

const FULL_ACCESS = {
    add: true,
    edit: true,
    draft: true,
    active: true,
    permission: true,
};

export const checkUserPermissionFunc = (MenuName, SubMenuName = null, Type) => {
    try {
        const isSuperAdmin = JSON.parse(sessionStorage.getItem("is_super_admin") || "false");
        const isSystemAdmin = JSON.parse(sessionStorage.getItem("is_system_admin") || "false");
        if (isSuperAdmin || isSystemAdmin) {
            return FULL_ACCESS;
        }

        let accessObject = undefined;
        let userPermissionList = JSON.parse(sessionStorage.getItem("loginUserPermission"));
        let accessData = patchMasterBlockFloorPermissions(
            userPermissionList?.length > 0 ? userPermissionList : []
        );
        let data = {}
        if (Type === 'Menu') {
            data = accessData.find(
                (list) => normalizePermKey(list.label) === normalizePermKey(MenuName)
            );
        } else if (Type === 'SubMenu') {
            const menu = accessData.find(
                (m) => normalizePermKey(m.label) === normalizePermKey(MenuName)
            );
            data = menu?.subMenu?.find(
                (item) => normalizePermKey(item.label) === normalizePermKey(SubMenuName)
            );

            // Compatibility with sessions that signed in BEFORE a module was
            // split from one flat screen into a subMenu (Roster was the first).
            // transformUserPermissionArr runs only at login and its output is
            // persisted, so a signed-in user keeps the old flat node — up to 12
            // hours, via the "keep me signed in" localStorage copy. Without this
            // fallback those users lose the screen entirely until they log in
            // again, and the symptom (a blank page, no error) is the hardest one
            // in this codebase to diagnose.
            //
            // Scoped tightly on purpose: only when the node has no subMenu at all
            // AND the module and submenu are the same name, which is exactly the
            // single-screen-module shape being migrated away from.
            if (!data && menu && !menu.subMenu
                && normalizePermKey(MenuName) === normalizePermKey(SubMenuName)) {
                data = menu;
            }
        }
        if (data) {
            accessObject = {
                ...data,
                add: !!(data.add || data.edit),
                edit: !!(data.edit || data.add),
                active: !!(data.active || data.edit || data.add),
            };
        }
        if (accessObject === undefined) {
            return {
                "add": false,
                "edit": false,
                "draft": false,
                "active": false,
                "permission": false
            }
        } else {
            return accessObject
        }
    }
    catch (e) {
        return {
            add: false,
            edit: false,
            draft: false,
            active: false,
            permission: false,
        };
    }
}

export const buildDynamicNav = (navItems, permissions, defaults) => {
    // An admin sees every menu, without needing each one granted.
    //
    // This mirrors the API, which has always bypassed the permission gate for
    // admins (authentication.jwt_authentication.IsAuthenticated returns True on
    // request.user.is_admin), and it mirrors the backend's own intent — an admin
    // group is created with default_permissions(is_admin=True), i.e. everything
    // allowed. Without the bypass here, a permission column added AFTER that
    // group was created is empty, so every newly built screen was hidden from
    // the administrator while its API worked perfectly. That mismatch is very
    // hard to diagnose from the outside: the menu is simply absent, with nothing
    // to indicate why.
    //
    // Only ever widens what is shown; a non-admin is filtered exactly as before.
    const isAdmin =
        JSON.parse(sessionStorage.getItem("is_super_admin") || "false") ||
        JSON.parse(sessionStorage.getItem("is_system_admin") || "false");

    return navItems
        .map((menu) => {
            const menuLabel = normalize(menu.label).toLowerCase();

            const menuPerm = permissions.find(
                (perm) => normalize(perm.label) === menuLabel
            );

            const isFullDefault = defaults.fullMenus?.includes(menuLabel);

            // allow menu if either permissions exist AND are true OR it's in defaults
            if (!isAdmin && (!menuPerm || menuPerm.permission === false) && !defaults[normalize(menu.label)] && !isFullDefault) {
                return null;
            }


            const hadSubItems = Array.isArray(menu.subItems) && menu.subItems.length > 0;
            let filteredSubItems = menu.subItems || [];

            if (filteredSubItems.length > 0) {
                filteredSubItems = filteredSubItems.filter((sub) => {
                    // A nav item may display one label but be permission-gated
                    // under a different permission key (e.g. "Employees" shown,
                    // gated by the "Employee List" permission). permKey wins.
                    const subLabel = normalize(sub.permKey || sub.label);

                    // keep always if default
                    const isDefault = defaults[normalize(menu.label)]?.includes(subLabel);
                    if (isDefault) return true;

                    // an admin sees every sub-item, entry present or not
                    // An admin sees every sub-item — see the note above. This is
                    // the line that was hiding newly added screens from the
                    // administrator even though their APIs were reachable.
                    if (isAdmin) return true;

                    // Cross-module items (e.g. Roster Report under Reports permissions)
                    const permSource = sub.permissionModule
                        ? permissions.find(
                            (perm) => normalize(perm.label) === normalize(sub.permissionModule)
                        )
                        : menuPerm;

                    const permitted = permSource?.subMenu?.some(
                        (permSub) =>
                            normalize(permSub.label) === subLabel && permSub.permission
                    );
                    if (!permitted) return false;

                    // The router also applies the route's roleGate, so a
                    // permission alone is not enough: without this the menu
                    // offers Team Review and Nine-Box to someone who is
                    // neither a reporting manager nor HR, and clicking either
                    // silently redirects them home.
                    return passesRouteRoleGate(sub.href);
                });
            }

            if (filteredSubItems.length > 0) {
                return { ...menu, subItems: filteredSubItems };
            }

            // Had sub-items but none are permitted -> hide the whole menu. Its href
            // is only a group header, so we must NOT fall back to the original
            // (unfiltered) sub-items here, which would leak denied links.
            if (hadSubItems) {
                return null;
            }

            // Genuine direct-link menu (no sub-items) -> show it as-is.
            return { ...menu };
        })
        .filter(Boolean);
};

export const emailValidation = async (_, value) => {
    const emailRegex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
    if (value && !emailRegex.test(value)) {
        return Promise.reject("Invalid Email ID");
    }
    return Promise.resolve();
};

/** Treat negative zero and tiny negative rounding as zero for payroll display. */
export const normalizePayrollAmount = (value) => {
    const amount = Number(value || 0);
    if (!Number.isFinite(amount) || amount <= 0) {
        return 0;
    }
    return amount;
};

export const formatPayrollCurrency = (value, fractionDigits = 2) =>
    normalizePayrollAmount(value).toLocaleString("en-IN", {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    });

export const formatDate = (timestamp) => {
    const date = new Date(timestamp);

    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "long" });
    const year = date.getFullYear();

    // Function to add ordinal suffix
    const getOrdinal = (n) => {
        if (n > 3 && n < 21) return n + "th"; // 4th–20th
        switch (n % 10) {
            case 1: return n + "st";
            case 2: return n + "nd";
            case 3: return n + "rd";
            default: return n + "th";
        }
    };

    return `${getOrdinal(day)} ${month} ${year}`;
}

export const isToday = (dateStr) => {
    const today = new Date();
    const d = new Date(dateStr);

    return (
        today.getDate() === d.getDate() &&
        today.getMonth() === d.getMonth()
    );
};

export const formatTimeToAMPM = (timeString) => {
    if (timeString === undefined || timeString === null || timeString === "") return "";
    const raw = String(timeString).trim();
    if (!raw || raw === "-" || raw === "--") return raw;
    const parsers = [
        moment(raw, "HH:mm:ss", true),
        moment(raw, "HH:mm", true),
        moment(raw, "H:mm:ss", true),
        moment(raw, "H:mm", true),
        moment(raw, "hh:mm A", true),
        moment(raw, "h:mm A", true),
        moment(raw, "YYYY-MM-DDTHH:mm:ss", true),
        moment(raw, "YYYY-MM-DD HH:mm:ss", true),
        moment(raw),
    ];
    const parsed = parsers.find((item) => item.isValid());
    return parsed ? parsed.format("h:mm A") : raw;
};

export const formatTimeRangeToAMPM = (from, to, separator = "–") => {
    const start = formatTimeToAMPM(from);
    const end = formatTimeToAMPM(to);
    if (!start && !end) return "";
    if (start && end) return `${start} ${separator} ${end}`;
    return start || end;
};

// print and download functions
export const getPrintData = async (url, query = {}) => {
    try {
        const authtoken = sessionStorage.getItem("token");

        // Build query params
        const queryParams = new URLSearchParams();
        Object.entries(query).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== "") {
                queryParams.append(key, value);
            }
        });
        queryParams.append("export_type", "pdf"); // always add pdf type

        const finalUrl = `${apiUrlFor(url)}?${queryParams.toString()}`;

        const requestOptions = {
            method: "GET",
            headers: { Authorization: `token ${authtoken}` },
        };

        const response = await fetch(finalUrl, requestOptions);
        if (!response.ok) {
            // The error body may be JSON ({ message }) OR an HTML page (a 404 /
            // 500 from nginx/Django). Parsing HTML as JSON throws the confusing
            // "Unexpected token '<'" error — so parse defensively and fall back
            // to a clean, status-based message.
            let message = `Request failed (${response.status}). Please try again later.`;
            try {
                const errorResponse = await response.json();
                if (errorResponse && errorResponse.message) message = errorResponse.message;
            } catch (_) {
                if (response.status === 404) message = "The requested resource was not found.";
                else if (response.status === 401 || response.status === 403) message = "You are not authorized to perform this action.";
            }
            throw new Error(message);
        }

        const fileBlob = await response.blob();
        const fileURL = URL.createObjectURL(fileBlob);
        window.open(fileURL);
    } catch (error) {
        ToastMsg("error", error.message);
    }
};

export const getCSVData = async (url, query = {}, type = "csv") => {
    try {
        const authtoken = sessionStorage.getItem("token");

        // Build query params
        const queryParams = new URLSearchParams();
        Object.entries(query).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== "") {
                queryParams.append(key, value);
            }
        });
        queryParams.append("export_type", type === "xlsx" ? "xlsx" : "csv");

        const finalUrl = `${apiUrlFor(url)}?${queryParams.toString()}`;

        const requestOptions = {
            method: "GET",
            headers: { Authorization: `token ${authtoken}` },
        };

        const response = await fetch(finalUrl, requestOptions);
        if (!response.ok) {
            // The error body may be JSON ({ message }) OR an HTML page (a 404 /
            // 500 from nginx/Django). Parsing HTML as JSON throws the confusing
            // "Unexpected token '<'" error — so parse defensively and fall back
            // to a clean, status-based message.
            let message = `Request failed (${response.status}). Please try again later.`;
            try {
                const errorResponse = await response.json();
                if (errorResponse && errorResponse.message) message = errorResponse.message;
            } catch (_) {
                if (response.status === 404) message = "The requested resource was not found.";
                else if (response.status === 401 || response.status === 403) message = "You are not authorized to perform this action.";
            }
            throw new Error(message);
        }

        // For CSV/XLSX binary data
        const blobData = await response.arrayBuffer();
        const csvBlob = new Blob([blobData], {
            type:
                type === "xlsx"
                    ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    : "text/csv",
        });

        const csvURL = URL.createObjectURL(csvBlob);

        const ext = type === "xlsx" ? "xlsx" : "csv";
        const fallbackName = (() => {
            const key = String(url || "").replace(/\/+$/, "");
            if (key.includes("employee/candidate/info")) return "Employee Onboarding";
            if (key.includes("employee/interns/info")) return "Intern Onboarding";
            if (key.includes("employee/info")) return "Employee Detailed Export";
            const last = key.split("/").filter(Boolean).pop() || "export";
            return last.replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase());
        })();
        const disposition = response.headers.get("Content-Disposition") || "";
        const starMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
        const quotedMatch = disposition.match(/filename="([^"]+)"/i);
        const plainMatch = disposition.match(/filename=([^;]+)/i);
        let fileName = fallbackName;
        if (starMatch) {
            try {
                fileName = decodeURIComponent(starMatch[1].trim());
            } catch (_) {
                fileName = starMatch[1].trim();
            }
        } else if (quotedMatch) {
            fileName = quotedMatch[1];
        } else if (plainMatch) {
            fileName = plainMatch[1].trim().replace(/^["']|["']$/g, "");
        }
        if (!fileName.toLowerCase().endsWith(`.${ext}`)) {
            fileName = `${fileName}.${ext}`;
        }

        // Trigger download
        const pom = document.createElement("a");
        pom.href = csvURL;
        pom.setAttribute("download", fileName);
        document.body.appendChild(pom);
        pom.click();
        document.body.removeChild(pom);
    } catch (error) {
        ToastMsg("error", error.message);
    }
};

export const downloadPDF = (url) => {
    const link = document.createElement('a');
    link.href = url;
    link.target = "_blank"
    const currentDate = new Date();
    const formattedDate = currentDate.toISOString().slice(0, 19).replace(/[-T]/g, '').replace(/:/g, '');
    link.setAttribute('download', `${formattedDate}.pdf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const _authHeader = () => {
    const authtoken = sessionStorage.getItem("token") || localStorage.getItem("token") || localStorage.getItem("accessToken");
    if (!authtoken) return {};
    const normalized = authtoken.toLowerCase().startsWith("token ") || authtoken.toLowerCase().startsWith("bearer ")
        ? authtoken
        : `token ${authtoken}`;
    return { Authorization: normalized };
};

/** Fetch a payslip PDF via the authenticated print endpoint and open or download it. */
export const openPayslipPdf = async (payslipId, { download = false, fileName = "payslip.pdf" } = {}) => {
    if (!payslipId) {
        ToastMsg("error", "Payslip not available.");
        return;
    }
    try {
        const query = new URLSearchParams({ id: String(payslipId) });
        const finalUrl = `${apiUrlFor(PAYROLL_PAYSLIP_PRINT)}?${query.toString()}`;
        const response = await fetch(finalUrl, {
            method: "GET",
            headers: _authHeader(),
        });
        if (!response.ok) {
            let message = `Request failed (${response.status}). Please try again later.`;
            try {
                const errorResponse = await response.json();
                if (errorResponse?.message) message = errorResponse.message;
            } catch (_) {
                if (response.status === 404) message = "The requested payslip was not found.";
                else if (response.status === 401 || response.status === 403) message = "You are not authorized to view this payslip.";
            }
            throw new Error(message);
        }

        const fileBlob = await response.blob();
        const fileURL = URL.createObjectURL(fileBlob);
        if (download) {
            const link = document.createElement("a");
            link.href = fileURL;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(fileURL);
        } else {
            window.open(fileURL);
        }
    } catch (error) {
        ToastMsg("error", error.message || "Failed to load payslip PDF.");
    }
};

export const truncateText = (text, maxLength = 40, tooltipMaxWidth = "300px", textWrap = "wrap", emptyMessage = '-') => {
    if (!text) return emptyMessage;
    return (
        <Tooltip>
            <TooltipTrigger>
                <span className=" cursor-pointer">
                    {text.length > maxLength ? `${text.slice(0, maxLength)}...` : text}
                </span>
            </TooltipTrigger>
            {text.length > maxLength ?
                <TooltipContent
                    side="bottom"
                    sideOffset={5}
                    transition={false}
                    maxWidth={tooltipMaxWidth}
                    textWrap={textWrap}
                    className="text-xs"
                >
                    {text}
                </TooltipContent>
                : null
            }
        </Tooltip>
    );
};

export const disabledPastDate = current => {
    // If the date is before today, disable it
    return current && current < moment().startOf('day');
};

export function convertToReadableFormat(str) {
    // Split the string by underscores and capitalize each word
    const specialCharsPattern = /[!@#$%^&*()_+{}\[\]:;<>,.?~\\]/g;
    // Use the match() method to find all occurrences of special characters
    const matches = str.match(specialCharsPattern);
    let words = str.split(matches).map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
    return words.join(' ');
}

export function numericRecordId(...values) {
    for (const value of values) {
        if (value == null || value === '') continue;
        if (typeof value === 'number' && Number.isFinite(value)) return value;
        if (typeof value === 'string' && /^\d+$/.test(value.trim())) return Number(value.trim());
    }
    return null;
}

export function formatTimeSpan(input) {
    if (input) {
        const [daysPart, timePart] = input.includes('days') || input.includes('day') ? input.split(', ') : ['0 days', input];
        const [daysStr] = daysPart.split(' ');
        const days = parseInt(daysStr, 10);
        const [hours, minutes, seconds] = timePart.split(/[:,]/).map(Number);
        const totalHours = days * 24 + hours;
        const formattedMinutes = String(minutes).padStart(2, '0');
        const formattedSeconds = String(seconds).padStart(2, '0');
        return `${totalHours}:${formattedMinutes}:${formattedSeconds}`;
    }
    return null;
}